import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ScryfallCard } from "@/types/scryfall";
import { MTGCard } from "../MTGCard";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Star,
  Sparkles, 
  Check,
  Search,
  Crown,
  HelpCircle,
  ThumbsUp
} from 'lucide-react';

interface SimplifiedCardSelectionStepProps {
  commander: ScryfallCard | null;
  colorIdentity: string[];
  themes: string;
  suggestedCards: ScryfallCard[];
  setSuggestedCards: (cards: ScryfallCard[]) => void;
  selectedCards: ScryfallCard[];
  setSelectedCards: (cards: ScryfallCard[]) => void;
  scryfallApiUrl: string;
  suggestedCommanders: ScryfallCard[];
  setCommander: (card: ScryfallCard | null) => void;
  cardTypes: string[];
  creatureTypes: string[];
}

export const SimplifiedCardSelectionStep = ({ 
  commander, 
  suggestedCards, 
  selectedCards, 
  setSelectedCards, 
  suggestedCommanders,
  setCommander,
}: SimplifiedCardSelectionStepProps) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (suggestedCards) {
      setLoading(false);
    }
  }, [suggestedCards]);

  const toggleCardSelection = (card: ScryfallCard) => {
    if (selectedCards.find(c => c.id === card.id)) {
      setSelectedCards(selectedCards.filter(c => c.id !== card.id));
    } else {
      setSelectedCards([...selectedCards, card]);
    }
  };

  // If no commander is selected, show commander suggestions
  if (!commander) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex items-center justify-center gap-2">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-amber-500/20 to-amber-600/30 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
            <Crown className="w-6 h-6 md:w-8 md:h-8 text-amber-500" />
          </div>
          <div className="text-left">
            <h2 className="text-lg md:text-xl font-bold">Commanders for You</h2>
            <p className="text-muted-foreground text-xs md:text-sm max-w-md">
              Based on your choices, here are some great commanders to lead your deck!
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {suggestedCommanders.map(c => (
            <Card 
              key={c.id} 
              className="cursor-pointer group transition-all duration-200 hover:scale-105 hover:shadow-lg touch-manipulation"
              onClick={() => setCommander(c)}
            >
              <CardContent className="p-4 text-center space-y-4">
                <div className="relative">
                  <div className="w-48 md:w-full mx-auto">
                    <MTGCard card={c} />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-end justify-center pb-4">
                    <Button size="sm" className="bg-primary hover:bg-primary/90">
                      <Crown className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Choose This Commander</span>
                      <span className="sm:hidden">Choose Commander</span>
                    </Button>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-sm">{c.name}</h3>
                  {c.mana_cost && (
                    <div className="text-xs text-muted-foreground font-mono mt-1">
                      {c.mana_cost}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-center gap-2">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-primary/20 to-primary/30 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
          <Star className="w-6 h-6 md:w-8 md:h-8 text-primary" />
        </div>
        <div className="text-left">
          <h2 className="text-lg md:text-xl font-bold">Recommended Cards</h2>
          <p className="text-muted-foreground text-xs md:text-sm max-w-md">
            Here are some popular cards that work well with your strategy. Pick the ones you like!
          </p>
        </div>
      </div>

      {/* Selection Summary */}
      {selectedCards.length > 0 && (
        <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="p-2 md:p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 md:gap-2">
                <ThumbsUp className="w-4 h-4 text-primary" />
                <span className="font-medium text-xs md:text-sm">
                  {selectedCards.length} card{selectedCards.length !== 1 ? 's' : ''} added to your deck
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedCards([])}
                className="text-xs h-6 md:h-7 px-2 md:px-3"
              >
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="space-y-6">
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <Sparkles className="absolute w-6 h-6 text-primary animate-pulse" />
            </div>
            <p className="mt-3 text-muted-foreground">
              Finding the perfect cards for your deck...
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[2.5/3.5] w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4 mx-auto" />
                <Skeleton className="h-8 w-full" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          {/* Card Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
            {suggestedCards.map(card => {
              const isSelected = selectedCards.find(c => c.id === card.id);
              return (
                <Card
                  key={card.id}
                  className={`cursor-pointer transition-all duration-200 hover:scale-105 group touch-manipulation ${
                    isSelected 
                      ? 'ring-2 ring-primary shadow-lg bg-primary/5' 
                      : 'hover:shadow-lg'
                  }`}
                >
                  <CardContent className="p-2 md:p-4 space-y-2 md:space-y-4">
                    <div className="relative">
                      <MTGCard card={card} />
                      
                      {/* Selection Indicator */}
                      {isSelected && (
                        <div className="absolute top-1 right-1 md:top-2 md:right-2 w-6 h-6 md:w-8 md:h-8 bg-primary rounded-full flex items-center justify-center shadow-lg">
                          <Check className="w-3 h-3 md:w-5 md:h-5 text-white" />
                        </div>
                      )}
                    </div>
                    
                    <div className="text-center space-y-2 md:space-y-3">
                      <div>
                        <h3 className="font-medium text-xs md:text-sm leading-tight">{card.name}</h3>
                        {card.mana_cost && (
                          <div className="text-xs text-muted-foreground font-mono mt-1">
                            {card.mana_cost}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-1 md:gap-2">
                        <Button
                          onClick={() => toggleCardSelection(card)}
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          className="flex-1 text-xs md:text-sm h-7 md:h-9 px-1 md:px-3"
                        >
                          {isSelected ? (
                            <>
                              <Check className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                              <span className="hidden sm:inline">Added</span>
                              <span className="sm:hidden">✓</span>
                            </>
                          ) : (
                            <>
                              <ThumbsUp className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                              <span className="hidden sm:inline">Add to Deck</span>
                              <span className="sm:hidden">Add</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* No Results */}
          {suggestedCards.length === 0 && (
            <Card className="max-w-2xl mx-auto border-dashed">
              <CardContent className="py-12 px-8 text-center space-y-4">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                  <Search className="w-8 h-8 text-muted-foreground" />
                </div>
                <div>
                  <h4 className="font-medium mb-2">No cards found</h4>
                  <p className="text-sm text-muted-foreground">
                    Try going back and selecting different colors or strategies.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Help Section */}
          <Card className="bg-muted/30">
            <CardContent className="p-3 md:p-6 text-center space-y-2 md:space-y-4">
              <div className="flex items-center justify-center gap-2">
                <HelpCircle className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
                <span className="font-medium text-sm md:text-base">Need help choosing?</span>
              </div>
              <div className="text-xs md:text-sm text-muted-foreground space-y-1 md:space-y-2">
                <p>• Add 3-6 cards that look interesting to you</p>
                <p>• Don't worry about getting it perfect - you can always change cards later</p>
                <p>• Focus on cards that sound fun to play with!</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};